// Per-creepy voices via the browser's built-in speech synthesis. Free, offline,
// no assets. Each anomaly carries pitch/rate/lang/gender so the 20 sound
// distinct — monstrous things drop low and slow, children pitch up, the frantic
// ones race. (A server-side proxy to voice.rogerthat.chat could be slotted in
// later behind a token; this is the always-works path.)

export interface VoiceSpec {
  pitch: number;
  rate: number;
  lang: "en" | "es";
  gender?: "male" | "female";
}

let voices: SpeechSynthesisVoice[] = [];
function refresh() {
  if ("speechSynthesis" in window) voices = window.speechSynthesis.getVoices();
}
if (typeof window !== "undefined" && "speechSynthesis" in window) {
  refresh();
  window.speechSynthesis.onvoiceschanged = refresh;
}

function pick(spec: VoiceSpec): SpeechSynthesisVoice | null {
  if (!voices.length) refresh();
  const want = spec.lang === "es" ? "es" : "en";
  const inLang = voices.filter((v) => v.lang.toLowerCase().startsWith(want));
  const pool = inLang.length ? inLang : voices;
  if (!pool.length) return null;
  // Cheap gender heuristic by common voice names.
  const female = /female|aria|jenny|zira|samantha|victoria|dalia|elvira|google us english/i;
  const male = /male|guy|david|mark|ryan|jorge|google uk english male/i;
  const rx = spec.gender === "female" ? female : spec.gender === "male" ? male : null;
  if (rx) {
    const m = pool.find((v) => rx.test(v.name));
    if (m) return m;
  }
  return pool[0];
}

let enabled = true;
export function setVoiceEnabled(on: boolean) {
  enabled = on;
  if (!on && "speechSynthesis" in window) window.speechSynthesis.cancel();
}

export type VoiceStage = "approaching" | "watching" | "present" | "withdrawing";

/** Speak a line in the anomaly's voice. Overlapping calls are cut off so the
 * newest line wins (a scare shouldn't queue up behind stale chatter).
 *
 * Stage-aware: while it's still APPROACHING/WATCHING at the threshold the voice
 * comes muffled and low — quieter, pitched down, slower, as if through glass or
 * across the room. Once it's PRESENT the voice is full and close. */
export function speak(text: string, spec: VoiceSpec, stage: VoiceStage = "present") {
  if (!enabled || !("speechSynthesis" in window) || !text) return;
  try {
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    // Distance sags the pitch and slows the words; proximity restores them.
    const pitchSag = stage === "approaching" ? 0.25 : stage === "watching" ? 0.12 : 0;
    const rateSag = stage === "approaching" ? 0.2 : stage === "watching" ? 0.1 : 0;
    u.pitch = Math.max(0, Math.min(2, spec.pitch - pitchSag));
    u.rate = Math.max(0.1, Math.min(2, spec.rate - rateSag));
    u.volume = stage === "approaching" ? 0.4 : stage === "watching" ? 0.7 : 1;
    u.lang = spec.lang === "es" ? "es-ES" : "en-US";
    const v = pick(spec);
    if (v) u.voice = v;
    window.speechSynthesis.speak(u);
  } catch {
    /* speech synthesis is best-effort */
  }
}
